import {
  AuditTrailReportOutput,
  ListReportsQuery,
  ReportOutput,
  RunRecordReportOutput,
  StabilityReportOutput,
} from '@eppendorf/vnls-reporting-service-utils';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import { fetcher } from '$shared/fetch';
import {
  AUDIT_TRAIL_REPORTS_BASE_PATH,
  createPathWithQueryParams,
  REPORTING_SERVICE_BASE_PATH,
  RUN_RECORD_REPORTS_BASE_PATH,
  STABILITY_REPORTS_BASE_PATH,
} from '$shared/utils/api-paths';

import { StabilityReportDetailModel } from '$features/report-hub/stability-report/stability-report.types';

export const reportsQueryKey = 'reports';
const REPORTS_PAGE_SIZE = 20;

// Generic infinite query factory — reusable for any reports list endpoint with the same params
export const createInfiniteReportsQuery = <T = ReportOutput>(
  basePath: string,
  queryKey: string,
  searchQuery?: string,
  status?: string,
  from?: string,
  to?: string,
) => ({
  queryKey: [queryKey, 'infinite', searchQuery, status, from, to],
  queryFn: async ({ pageParam = 0 }) => {
    const params: ListReportsQuery = {
      limit: REPORTS_PAGE_SIZE,
      offset: pageParam,
      searchQuery,
      status: status as ListReportsQuery['status'],
      from,
      to,
    };

    return fetcher.get<T[]>(createPathWithQueryParams(basePath, params));
  },
  getNextPageParam: (lastPage: T[], pages: T[][]) => {
    if (!lastPage || lastPage.length < REPORTS_PAGE_SIZE) {
      return undefined;
    }
    return pages.length * REPORTS_PAGE_SIZE;
  },
  initialPageParam: 0,
  refetchInterval: 10000,
});

export const auditTrailReportsQueryKey = 'auditTrailReports';

export const useInfiniteAuditTrailReports = (
  searchQuery?: string,
  status?: string,
  from?: string,
  to?: string,
) =>
  useInfiniteQuery(
    createInfiniteReportsQuery<AuditTrailReportOutput>(
      AUDIT_TRAIL_REPORTS_BASE_PATH,
      auditTrailReportsQueryKey,
      searchQuery,
      status,
      from,
      to,
    ),
  );

export const stabilityReportsQueryKey = 'stabilityReports';

export const runRecordReportsQueryKey = 'runRecordReports';

export const useInfiniteRunRecordReports = (
  searchQuery?: string,
  status?: string,
  from?: string,
  to?: string,
) =>
  useInfiniteQuery(
    createInfiniteReportsQuery<RunRecordReportOutput>(
      RUN_RECORD_REPORTS_BASE_PATH,
      runRecordReportsQueryKey,
      searchQuery,
      status,
      from,
      to,
    ),
  );

export const useInfiniteStabilityReports = (
  searchQuery?: string,
  status?: string,
  from?: string,
  to?: string,
) =>
  useInfiniteQuery(
    createInfiniteReportsQuery<StabilityReportOutput>(
      STABILITY_REPORTS_BASE_PATH,
      stabilityReportsQueryKey,
      searchQuery,
      status,
      from,
      to,
    ),
  );

// Download report - returns the presigned URL
export const downloadReport = async (reportId: string): Promise<string> => {
  const response = await fetcher.get<{ presignedUrl: string }>(
    `${REPORTING_SERVICE_BASE_PATH}/${reportId}/download`,
  );
  return response.presignedUrl;
};

export const useDownloadReport = () =>
  useMutation<string, AxiosError, string>({
    mutationFn: downloadReport,
  });

// Delete report mutation
export const useDeleteReport = () => {
  const deleteReport = (reportId: string) =>
    fetcher.delete<never>(`${REPORTING_SERVICE_BASE_PATH}/${reportId}`);

  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<never>, AxiosError, string>({
    mutationFn: deleteReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [reportsQueryKey] });
    },
  });
};

// Get single stability report by id
export const createGetStabilityReportQuery = (reportId: string) => ({
  queryKey: [stabilityReportsQueryKey, reportId],
  queryFn: () =>
    fetcher.get<StabilityReportDetailModel>(`${REPORTING_SERVICE_BASE_PATH}/${reportId}`),
});

export const useGetStabilityReport = (reportId: string | null) =>
  useQuery<StabilityReportDetailModel>({
    ...createGetStabilityReportQuery(reportId!),
    enabled: !!reportId,
  });
